import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Upload, FileSpreadsheet, RefreshCw, CheckCircle, AlertTriangle, Clock,
  Users, MapPin, Package, Percent, Truck, UserCircle, Tag,
} from "lucide-react";
import { toast } from "sonner";

const IMPORT_DEFS = [
  {
    key: "clients",
    label: "Clienti",
    icon: Users,
    color: "emerald",
    endpoint: "/clients/import",
    accept: ".xls,.xlsx,.csv",
    columns: [
      "codice_cli", "ragione_sociale", "indirizzo", "cap", "citta", "provincia",
      "partita_iva", "codice_fiscale", "telefono", "email",
    ],
    description: "Anagrafica clienti master. Sovrascrive i campi del cliente esistente per codice_cli.",
  },
  {
    key: "destinations",
    label: "Destinazioni",
    icon: MapPin,
    color: "cyan",
    endpoint: "/destinations/import",
    accept: ".xls,.xlsx,.csv",
    columns: [
      "codice_cli", "prog_destin", "denominaz", "indirizzo", "localita",
      "cap", "provincia", "nazione", "telefono",
    ],
    description: "Indirizzi alternativi (filiali, magazzini). Si associano ai clienti via codice_cli.",
  },
  {
    key: "agents",
    label: "Agenti",
    icon: UserCircle,
    color: "purple",
    endpoint: "/agents/import",
    accept: ".xls,.xlsx,.csv",
    columns: ["codice_cli", "cod_agente"],
    description: "Mapping cliente → agente di vendita. Aggiorna il campo cod_agente sul cliente.",
  },
  {
    key: "products",
    label: "Prodotti",
    icon: Package,
    color: "blue",
    endpoint: "/products/import",
    accept: ".xls,.xlsx,.csv",
    columns: [
      "sku", "name", "category", "brand", "price_list", "price_purchase",
      "price_min", "price_auth", "price_promo", "price_list_2", "unit", "iva",
    ],
    description: "Catalogo articoli. Crea nuovi se non esistono, aggiorna esistenti per sku.",
  },
  {
    key: "discount_rules",
    label: "Sconti / Listini",
    icon: Percent,
    color: "amber",
    endpoint: "/discount-rules/import",
    accept: ".xls,.xlsx,.csv",
    columns: [
      "cod_cliente", "category_id|category_code|cod_articolo|brand_code",
      "sc1", "sc2", "sc3", "sc4", "sconto_canale", "priority",
    ],
    description: "Regole sconto cascading per cliente e categoria/marca/articolo.",
  },
  {
    key: "giri",
    label: "Giri (ZonaCliDestMasc)",
    icon: Truck,
    color: "orange",
    endpoint: "/giri/import",
    accept: ".csv,.xls,.xlsx",
    columns: [
      "Cod_Agente", "Cod_Giorno", "Num_Giro", "Codice_Cli", "Denominaz",
      "Indirizzo", "Localita", "Cod_Articolo", "Descrizione",
      "UltimoPrezzo", "UltimoDiqta", "UltimoDidata_emiss",
      "UltimoDiqta_R", "UltimoDidata_emiss_R",
    ],
    description: "Routing settimanale agenti + storico ultimi prezzi e resi per cliente/articolo.",
  },
  {
    key: "promotions",
    label: "Promozioni",
    icon: Tag,
    color: "red",
    endpoint: "/promotions/import",
    accept: ".csv,.xls,.xlsx",
    columns: [
      "cod_int_promo", "cod_cliente", "cod_articolo", "categ_merc", "marca", "cod_categ",
      "data_inizio", "data_termine", "sconto_1", "sconto_2", "sconto_3", "sconto_4",
      "prezzo_netto", "qta_omaggio", "multiplo_di", "descr_promo",
    ],
    description: "Promozioni a tempo per cliente. Target: articolo, categoria merc., marca o cod. categ. pagamento. ATTENZIONE: l'import sostituisce TUTTE le promozioni esistenti.",
  },
];

const COLOR_CLASSES = {
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300", btn: "bg-emerald-500 hover:bg-emerald-600 text-black" },
  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-300", btn: "bg-cyan-500 hover:bg-cyan-600 text-black" },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-300", btn: "bg-purple-500 hover:bg-purple-600 text-white" },
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-300", btn: "bg-blue-500 hover:bg-blue-600 text-white" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-300", btn: "bg-amber-500 hover:bg-amber-600 text-black" },
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-300", btn: "bg-orange-500 hover:bg-orange-600 text-white" },
  red: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-300", btn: "bg-red-500 hover:bg-red-600 text-white" },
};

function StubsCleanupCard({ defKey }) {
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get("/products/stubs/stats");
      setStats(r.data);
    } catch { setStats(null); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  // Mostra solo nella tab prodotti
  if (defKey !== "products") return null;
  if (!stats || stats.total_stubs === 0) return null;

  const handlePromote = async () => {
    if (!stats.promotable) return;
    setBusy(true);
    const tid = toast.loading("Promozione stub in corso...");
    try {
      const r = await authAxios.post("/products/stubs/promote");
      toast.success(`${r.data.promoted} prodotti promossi da stub a definitivi`, { id: tid });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore", { id: tid });
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-amber-500/5 border-amber-500/30" data-testid="stubs-cleanup-card">
      <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <AlertTriangle className="w-7 h-7 text-amber-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-amber-200 font-semibold text-sm">
            {stats.total_stubs} prodotti stub presenti in catalogo
          </p>
          <p className="text-zinc-400 text-xs mt-0.5">
            Sono prodotti placeholder creati dalla vista &ldquo;Mancanti&rdquo; delle Statistiche.
            {stats.promotable > 0 ? (
              <> <strong className="text-amber-200">{stats.promotable}</strong> hanno già tutti i dati e possono essere promossi a definitivi (saranno visibili nel catalogo).</>
            ) : (
              <> Nessuno è ancora promovibile: aspettano di essere riempiti da un import del file Articoli.</>
            )}
          </p>
        </div>
        {stats.promotable > 0 && (
          <Button
            onClick={handlePromote}
            disabled={busy}
            data-testid="promote-stubs-btn"
            className="bg-amber-500 hover:bg-amber-600 text-black shrink-0"
          >
            {busy ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Promuovi {stats.promotable}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ImportPanel({ def, onComplete }) {
  const { authAxios } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [uploadPct, setUploadPct] = useState(null); // null | 0..100 while uploading
  const [history, setHistory] = useState([]);
  const colors = COLOR_CLASSES[def.color];

  const loadHistory = async () => {
    try {
      const res = await authAxios.get(`/import-logs?type=${def.key}&limit=5`);
      setHistory(res.data.items || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setJobProgress(null);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authAxios.post(def.endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadPct(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      setUploadPct(null);
      // If backend returned an async job, poll status until completion
      if (res.data?.job_id) {
        const jobId = res.data.job_id;
        const statusUrl = `${def.endpoint}/status/${jobId}`;
        // Show immediate "starting" state until first poll succeeds
        setJobProgress({ phase: "queued", progress_current: 0, progress_total: 0 });
        toast.info("Import in corso in background…");
        // Polling loop (every 2s, max 30 min)
        const startedAt = Date.now();
        const MAX_WAIT_MS = 30 * 60 * 1000;
        while (Date.now() - startedAt < MAX_WAIT_MS) {
          await new Promise(r => setTimeout(r, 2000));
          let jres;
          try {
            jres = await authAxios.get(statusUrl);
          } catch (e) {
            console.warn("poll failed", e?.message);
            continue;
          }
          setJobProgress(jres.data);
          if (jres.data.status === "completed") {
            setResult(jres.data.result || { message: "Importazione completata" });
            toast.success(jres.data.result?.message || "Importazione completata");
            setFile(null);
            setJobProgress(null);
            loadHistory();
            onComplete?.(def.key, jres.data.result);
            break;
          }
          if (jres.data.status === "failed") {
            const detail = jres.data.error || "Errore durante l'import";
            toast.error(detail);
            setResult({ error: detail });
            setJobProgress(null);
            break;
          }
        }
      } else {
        setResult(res.data);
        toast.success(res.data.message || "Importazione completata");
        setFile(null);
        loadHistory();
        onComplete?.(def.key, res.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Errore";
      toast.error(detail);
      setResult({ error: detail });
      setUploadPct(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <StubsCleanupCard defKey={def.key} />
      <Card className={`${colors.bg} ${colors.border}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <def.icon className={`w-5 h-5 mt-0.5 ${colors.text}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${colors.text}`}>{def.label}</p>
              <p className="text-xs text-zinc-400 mt-1">{def.description}</p>
              <p className="text-[11px] text-zinc-500 mt-2">
                <span className="text-zinc-400">Colonne attese:</span>{" "}
                {def.columns.map((c) => (
                  <code key={`col-${c}`} className="bg-black/40 px-1 py-[1px] rounded text-[10px] mr-1 inline-block mb-0.5">{c}</code>
                ))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#18181b] border-white/5">
        <CardContent className="p-6">
          <div className="border-2 border-dashed rounded-xl p-8 text-center border-white/10 hover:border-white/20 transition-colors">
            <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-300 mb-2">Trascina il file qui oppure</p>
            <label className="cursor-pointer">
              <Button variant="outline" className={`${colors.text} ${colors.border} hover:${colors.bg}`} asChild>
                <span>Seleziona File</span>
              </Button>
              <input type="file" accept={def.accept} className="hidden"
                data-testid={`import-file-${def.key}`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); setResult(null); }
                }} />
            </label>
            <p className="text-zinc-500 text-xs mt-2">Formati: {def.accept}</p>
          </div>

          {file && (
            <div className="mt-4 flex items-center justify-between bg-[#27272a] rounded-lg p-3 gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <FileSpreadsheet className={`w-5 h-5 shrink-0 ${colors.text}`} />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{file.name}</p>
                  <p className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => { setFile(null); setResult(null); }} className="text-zinc-400">
                  Rimuovi
                </Button>
                <Button size="sm" onClick={handleImport} disabled={busy} className={colors.btn}
                  data-testid={`import-btn-${def.key}`}>
                  {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {busy ? "Importando..." : "Importa"}
                </Button>
              </div>
            </div>
          )}

          {uploadPct !== null && (
            <Card data-testid={`upload-progress-${def.key}`} className="mt-4 bg-[#27272a] border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-amber-400 animate-spin shrink-0" />
                  <p className="text-white text-sm font-medium flex-1">
                    Caricamento file in corso… {file?.size ? `(${(file.size / 1024 / 1024).toFixed(1)} MB)` : ""}
                  </p>
                  <span className="text-xs font-mono font-bold text-amber-400">{uploadPct}%</span>
                </div>
                <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${uploadPct}%` }}
                  />
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  💡 Non chiudere la pagina finché il caricamento non è completo.
                </p>
              </CardContent>
            </Card>
          )}

          {jobProgress && (
            <Card data-testid={`import-progress-${def.key}`} className="mt-4 bg-[#27272a] border-blue-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                  <p className="text-white text-sm font-medium flex-1">
                    {jobProgress.phase === "reading_file" && "Lettura file…"}
                    {jobProgress.phase === "parsing" && `Elaborazione righe… ${jobProgress.progress_current?.toLocaleString("it-IT") || 0}/${jobProgress.progress_total?.toLocaleString("it-IT") || "?"}`}
                    {jobProgress.phase === "inserting" && `Salvataggio fermate… ${jobProgress.progress_current?.toLocaleString("it-IT") || 0}/${jobProgress.progress_total?.toLocaleString("it-IT") || "?"}`}
                    {jobProgress.phase === "swapping" && "Finalizzazione…"}
                    {jobProgress.phase === "queued" && "In coda…"}
                    {jobProgress.phase === "done" && "Completato"}
                  </p>
                  {jobProgress.progress_total > 0 && (
                    <span className="text-xs font-mono font-bold text-blue-400">
                      {Math.round((jobProgress.progress_current / jobProgress.progress_total) * 100)}%
                    </span>
                  )}
                </div>
                <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{
                      width: jobProgress.progress_total > 0
                        ? `${Math.min(100, (jobProgress.progress_current / jobProgress.progress_total) * 100)}%`
                        : "10%"
                    }}
                  />
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  💡 Puoi continuare a usare l'app, l'import prosegue in background.
                </p>
              </CardContent>
            </Card>
          )}

          {result && !result.error && (
            <Card className="mt-4 bg-[#27272a] border-emerald-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <p className="text-white text-sm font-medium">Importazione completata</p>
                </div>
                <div className="text-xs text-zinc-400 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(result).filter(([k]) => !["errors", "message", "errors_total"].includes(k)).map(([k, v]) => (
                    <div key={k} className="bg-black/30 rounded px-2 py-1">
                      <span className="text-zinc-500">{k}:</span> <span className="text-white font-mono">{Array.isArray(v) ? v.length : String(v)}</span>
                    </div>
                  ))}
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-y-auto text-xs text-amber-300 space-y-1">
                    <p className="text-amber-400 font-semibold">Avvisi:</p>
                    {result.errors.slice(0, 10).map((e) => {
                      const msg = typeof e === "string" ? e : JSON.stringify(e);
                      return <p key={`imp-err-${msg.slice(0, 96)}`}>• {msg}</p>;
                    })}
                    {result.errors.length > 10 && <p className="italic">... e altri {result.errors.length - 10}</p>}
                  </div>
                )}
                {/* Righe saltate dall'import Giri (ZonaCliDestMasc): mostra motivi + campioni */}
                {result.skipped_total > 0 && (
                  <div className="mt-3 bg-amber-500/10 border border-amber-500/40 rounded p-3" data-testid="skipped-rows-warning">
                    <p className="text-amber-300 font-semibold text-sm mb-1">
                      ⚠ {result.skipped_total} righe saltate durante l&apos;import (su {result.total_rows} totali)
                    </p>
                    {result.skipped_by_reason && (
                      <div className="text-xs text-zinc-400 mb-2 grid grid-cols-1 sm:grid-cols-3 gap-1">
                        {Object.entries(result.skipped_by_reason).filter(([, v]) => v > 0).map(([k, v]) => (
                          <div key={k} className="bg-black/30 rounded px-2 py-1">
                            <span className="text-zinc-500">{k}:</span>{" "}
                            <span className="text-amber-200 font-mono">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {result.skipped_samples?.length > 0 && (
                      <details className="text-xs text-amber-200 mt-1">
                        <summary className="cursor-pointer text-amber-300">Mostra i primi {result.skipped_samples.length} esempi</summary>
                        <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
                          {result.skipped_samples.map((s, i) => (
                            <div key={`skip-${i}`} className="font-mono">
                              riga {s.row}: {s.reason} · Cli={s.Cod_Cliente || "—"} · Ag={s.Cod_Agente || "—"} · G={s.Cod_Giorno || "—"} · {s.denominaz?.slice(0, 40)}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                {/* Avviso SKU mancanti post-import (verifica) */}
                {result.missing_skus?.length > 0 && (
                  <div className="mt-3 bg-orange-500/10 border border-orange-500/40 rounded p-3" data-testid="missing-skus-warning">
                    <p className="text-orange-300 font-semibold text-sm mb-1">
                      ⚠ {result.missing_skus.length} articoli del file NON sono nel catalogo
                    </p>
                    <p className="text-zinc-400 text-xs mb-2">
                      Letti dal file: {result.file_skus_count} · Presenti in DB: {result.db_skus_present}
                    </p>
                    <p className="text-orange-200 text-xs font-mono break-all">
                      {result.missing_skus.slice(0, 20).join(", ")}
                      {result.missing_skus.length > 20 && ` ... +${result.missing_skus.length - 20}`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {result?.error && (
            <Card className="mt-4 bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <p className="text-red-300 text-sm">{result.error}</p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Storico */}
      <Card className="bg-[#18181b] border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-zinc-400" />
            <p className="text-sm text-zinc-300 font-medium">Ultimi 5 import</p>
          </div>
          {history.length === 0 ? (
            <p className="text-zinc-500 text-xs italic">Nessun import registrato per questo tipo.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-2 text-xs bg-black/30 rounded px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white truncate">
                      {new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      <span className="text-zinc-500 ml-2 font-mono">{h.file_name || "—"}</span>
                    </p>
                    <p className="text-zinc-500 truncate">
                      {h.user_name && <span className="text-zinc-400">{h.user_name}</span>}
                      {h.result && (
                        <span className="ml-2">
                          {Object.entries(h.result).filter(([k, v]) => typeof v === "number" || typeof v === "string").slice(0, 3).map(([k, v]) => (
                            <span key={k} className="mr-2"><span className="text-zinc-600">{k}</span> <span className="text-zinc-300 font-mono">{String(v)}</span></span>
                          ))}
                        </span>
                      )}
                    </p>
                  </div>
                  {h.success ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminImports() {
  const [tab, setTab] = useState("clients");

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <PageHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Centro Importazioni</h1>
          <p className="text-zinc-400 text-sm">Tutti gli import in un unico posto, con storico delle operazioni.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="w-full overflow-x-auto flex bg-[#18181b] border border-white/5 p-1 gap-1">
            {IMPORT_DEFS.map((d) => {
              const c = COLOR_CLASSES[d.color];
              return (
                <TabsTrigger key={d.key} value={d.key}
                  className={`shrink-0 data-[state=active]:${c.btn.split(" ")[0]} data-[state=active]:${c.text}`}
                  data-testid={`tab-import-${d.key}`}>
                  <d.icon className="w-4 h-4 mr-1.5" />
                  {d.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {IMPORT_DEFS.map((d) => (
            <TabsContent key={d.key} value={d.key}>
              <ImportPanel def={d} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
